import { Text } from '@youversion/design-system-components';
import ChevronLeftStroked from '@youversion/design-system-icons/lib/ChevronLeftStroked';
import { useTranslation } from 'next-i18next';
import React from 'react';

import ButtonBase from './button-base';
import { BaseButtonProps } from './types';

/**
 * Returns an instance of a BackButton.
 *
 * @param btnText - Contents of the button.
 * @param className - Class name(s) to apply.
 * @param disabled - If true, sets `disabled` property on the button.
 * @param id - The value to set for the `id` property.
 * @param name - The value to set for the `name` property.
 * @param onClick - The callback handler for the click event.
 * @param type - Type property. Must be `button`, `reset`, or `submit`.
 * @param value - Value to set on the button.
 * @param props - Remaining props passed to ButtonBase.
 * @returns JSX.Element.
 *
 * @example
 *
 * ```typescriptreact
 * <BackButton
 *   onClick={() => {}}
 * />
 * ```
 */
export function BackButton({
  btnText,
  className,
  disabled,
  id,
  name,
  onClick,
  type,
  value,
  ...props
}: BaseButtonProps) {
  const { t } = useTranslation();

  return (
    <ButtonBase
      className={`dark:text-text-dark text-button back-button flex w-fit ${className ? `${className}` : ''}`}
      disabled={disabled}
      id={id}
      name={name}
      onClick={onClick}
      title={btnText || t('giving.navigation.back')}
      type={type}
      value={value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <>
        <ChevronLeftStroked className='pie-0.5 text-black dark:text-white' />
        {btnText || <Text className='back-label'>{t('giving.navigation.back')}</Text>}
      </>
    </ButtonBase>
  );
}
